'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/probability'

const DiceSums = dynamic(() => import('../_components/widgets/ProbWidgets').then(m => m.DiceSums), { ssr: false })
const NormalExplorer = dynamic(() => import('../_components/widgets/ProbWidgets').then(m => m.NormalExplorer), { ssr: false })
const SpreadSquares = dynamic(() => import('../_components/widgets/ProbWidgets').then(m => m.SpreadSquares), { ssr: false })

export default function Distributions() {
  return (
    <Lesson>
      <Step gate="q-bell">
        <H>The shape of chance</H>
        <P>
          A <strong>distribution</strong> lists every possible outcome together with how likely it is. The easiest way to see
          one is a <strong>histogram</strong>: run an experiment many times and draw a bar for how often each result came up.
        </P>
        <Figure n="5.4" caption="Roll dice and add them up. With 1 die every result is equally likely (flat). With more dice, a bell shape appears — middle sums have many more ways to happen.">
          <DiceSums />
        </Figure>
        <Quiz
          id="q-bell"
          question="Why does the sum of 10 dice almost never come out as 10 or 60?"
          options={[
            { label: 'There’s only one way to get each (all 1s or all 6s), but millions of ways to get a middle total.', correct: true },
            { label: 'Dice are designed to avoid extreme results.', why: 'Each die is fair — it’s about counting combinations.' },
            { label: 'It’s impossible.', why: 'It’s possible, just very unlikely: (1/6)¹⁰ ≈ 1 in 60 million.' },
          ]}
        />
      </Step>

      <Step>
        <H>The bell curve</H>
        <P>
          That bell shape shows up whenever many small random things add together — heights, measurement errors, test scores.
          It’s called the <strong>normal distribution</strong> (or Gaussian), and it has just two knobs:
        </P>
        <ul className="gd-list">
          <li>
            <M>{'\\amber{\\mu}'}</M> (“mu”), the <strong>mean</strong>: where the centre is.
          </li>
          <li>
            <M>{'\\violet{\\sigma}'}</M> (“sigma”), the <strong>standard deviation</strong>: how spread out it is.
          </li>
        </ul>
        <Figure n="5.5" caption="Move the centre and change the spread. About 68% of values fall within one σ of the mean, 95% within two, 99.7% within three.">
          <NormalExplorer />
        </Figure>
        <Exercise id="histogram" title="Sampling a bell curve" {...EX.histogram} />
      </Step>

      <Step gate="q-var">
        <H>Measuring spread</H>
        <P>
          How do we put a number on “spread out”? Measure how far each value is from the mean, square it (so left and right both
          count — sound familiar?), and average. That’s the <strong>variance</strong>:
        </P>
        <MB>{'\\text{Var}(x) = \\frac{1}{n}\\sum_{i=1}^{n} \\left(x_i - \\amber{\\bar{x}}\\right)^2 \\qquad \\violet{\\sigma} = \\sqrt{\\text{Var}(x)}'}</MB>
        <P>The standard deviation σ is its square root, which brings it back to the original units.</P>
        <Figure n="5.6" caption="Each pink square is one value’s squared distance from the mean. Variance is the average square.">
          <SpreadSquares />
        </Figure>
        <Quiz
          id="q-var"
          question="Two datasets have the same mean. One has variance 1, the other variance 25. What’s different?"
          options={[{ label: 'The second is much more spread out (std 5 vs 1).', correct: true }, { label: 'The second has bigger values on average.', why: 'Same mean — same average. Variance only measures spread.' }, { label: 'The second has more data points.', why: 'Variance doesn’t depend on how many points there are.' }]}
        />
      </Step>

      <Step gate={['variance-std', 'standardize']}>
        <H>Write them</H>
        <Exercise
          id="variance-std"
          title="variance and std"
          {...EX.varianceStd}
          hints={[
            <>
              Get the mean with <C>vec.mean(xs)</C>, then average <C>(x - m) ** 2</C>.
            </>,
          ]}
        />
        <P>
          One of the most common moves in practice: <strong>standardise</strong> each feature so it has mean 0 and standard
          deviation 1. A house’s size (in the hundreds) and number of rooms (single digits) then live on the same scale, and
          gradient descent behaves much better.
        </P>
        <Exercise id="standardize" title="Standardise a feature" {...EX.standardize} hints={[<>Compute the mean and std first, then <C>{'xs.map(x => (x - m) / s)'}</C>.</>]} />
        <Callout kind="why">
          Distributions are everywhere in AI. Network weights start as random samples from a bell curve. Data gets
          standardised. And a model’s output is itself a distribution over possible answers — which is the next lesson.
        </Callout>
      </Step>
    </Lesson>
  )
}
